import { ChevronDown, ChevronUp, User, Truck, Users, Plus, Trash2, Minus, DollarSign, ImageOff, Loader2, FileCheck2, Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'

import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import { Button } from '@/app/components/ui/button'
import { Checkbox } from '@/app/components/ui/checkbox'
import { isPromotionPricing, pricingRuleLabel, pricingRuleName } from '@/app/lib/pricing-display'
import { type CartItem, type CartPrescription, type DeliveryDetails, type PosCartData } from '@/app/services/pos.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

export interface PosCartProps {
  saleType: 'regular' | 'delivery'
  setSaleType: (type: 'regular' | 'delivery') => void
  deliveryDetails: DeliveryDetails
  setShowDeliveryDetailsModal: (show: boolean) => void
  customerType: 'general' | 'registered'
  setCustomerType: (type: 'general' | 'registered') => void
  selectedCustomer: number | null
  setSelectedCustomer: (id: number) => void
  setShowNewCustomerModal: (show: boolean) => void
  customerPoints: number
  loyaltyCredit: number
  hasAvailableReward: boolean
  nextReward: { pointsRequired: number; creditAmount: number } | null
  pointsToNextReward: number | null
  loyaltyEnabled: boolean
  isQuotingLoyalty: boolean
  pointsEarned: number
  usePoints: boolean
  setUsePoints: (use: boolean) => void
  cart: CartItem[]
  clearCart: () => void
  deleteSelectedItems: () => void
  toggleSelectItem: (id: number) => void
  removeFromCart: (id: number) => void
  updateQuantity: (id: number, delta: number) => void
  subtotal: number
  discountTotal?: number
  regularTotal?: number
  savingsTotal?: number
  tax: number
  deliveryCost: number
  pointsDiscount: number
  total: number
  onProcessPayment: () => void
  isValidatingCart: boolean
  canCompleteSale?: boolean
  isUpdatingQuantities?: boolean
  isProductQuantityPending?: (id: number) => boolean
  prescriptions?: CartPrescription[]
  regulatory?: PosCartData['regulatory']
  onEditPrescription?: () => void
}

export function PosCart({
  saleType,
  setSaleType,
  deliveryDetails,
  setShowDeliveryDetailsModal,
  customerType,
  setCustomerType,
  selectedCustomer,
  setSelectedCustomer,
  setShowNewCustomerModal,
  customerPoints,
  loyaltyCredit,
  hasAvailableReward,
  nextReward,
  pointsToNextReward,
  loyaltyEnabled,
  isQuotingLoyalty,
  pointsEarned,
  usePoints,
  setUsePoints,
  cart,
  clearCart,
  deleteSelectedItems,
  toggleSelectItem,
  removeFromCart,
  updateQuantity,
  subtotal,
  regularTotal = 0,
  savingsTotal = 0,
  tax,
  deliveryCost,
  pointsDiscount,
  total,
  onProcessPayment,
  isValidatingCart,
  canCompleteSale = true,
  isUpdatingQuantities = false,
  isProductQuantityPending = () => false,
  prescriptions,
  regulatory,
  onEditPrescription,
}: PosCartProps) {
  const canAction = useSessionStore((s) => s.canAction)
  const [customerSummaryExpanded, setCustomerSummaryExpanded] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 1024px)').matches
  ))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const desktopQuery = window.matchMedia('(min-width: 1024px)')
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setCustomerSummaryExpanded(event.matches)
    }

    desktopQuery.addEventListener('change', handleBreakpointChange)
    return () => desktopQuery.removeEventListener('change', handleBreakpointChange)
  }, [])
  const hasCart = cart.length > 0
  const canUseLoyalty = loyaltyCredit > 0 || hasAvailableReward
  const loyaltyCheckDisabled = !hasCart || isQuotingLoyalty || !canUseLoyalty
  const formattedNextReward = nextReward
    ? `${nextReward.pointsRequired.toLocaleString('es-MX')} puntos por $${nextReward.creditAmount.toLocaleString('es-MX')}`
    : null

  let loyaltyMessage: string | null = null
  if (!hasCart) {
    loyaltyMessage = 'Agrega productos para calcular el canje.'
  } else if (!isQuotingLoyalty && !loyaltyEnabled) {
    loyaltyMessage = 'Programa desactivado; solo puedes usar saldo existente.'
  } else if (!isQuotingLoyalty && !hasAvailableReward && nextReward && pointsToNextReward !== null) {
    loyaltyMessage = usePoints && loyaltyCredit > 0
      ? `Se aplicará tu saldo disponible; te faltan ${pointsToNextReward.toLocaleString('es-MX')} puntos para la siguiente recompensa.`
      : `Te faltan ${pointsToNextReward.toLocaleString('es-MX')} puntos para canjear ${formattedNextReward}.`
  } else if (!isQuotingLoyalty && !hasAvailableReward && !nextReward) {
    loyaltyMessage = usePoints && loyaltyCredit > 0
      ? 'Se aplicará únicamente tu saldo; no hay recompensas activas disponibles.'
      : 'No hay recompensas activas disponibles.'
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col border-l border-border bg-card text-card-foreground lg:w-[450px]">
      <div className="shrink-0 space-y-3 border-b border-border p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-card-foreground">Carrito de Venta</h2>
          {cart.length > 0 && (
            <button
              onClick={clearCart}
              disabled={isUpdatingQuantities}
              className="text-card-description hover:text-red-500 transition-colors"
              title="Vaciar carrito"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sale Type */}
        <div className="flex gap-2">
          <button
            onClick={() => setSaleType('regular')}
            className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${saleType === 'regular'
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-white text-slate-900 hover:border-primary/50 hover:bg-primary/5'
              }`}
          >
            <User className="mx-auto mb-1 h-4 w-4" />
            <span className="text-xs font-medium">Venta en Tienda</span>
          </button>
          <button
            onClick={() => setSaleType('delivery')}
            className={`flex-1 rounded-lg border-2 p-2 text-sm transition-all ${saleType === 'delivery'
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-slate-200 hover:border-slate-300'
              }`}
          >
            <Truck className="mx-auto mb-1 h-4 w-4" />
            <span className="text-xs font-medium">A Domicilio</span>
          </button>
        </div>

        {/* Delivery details */}
        {saleType === 'delivery' && (
          <div>
            <Button
              onClick={() => setShowDeliveryDetailsModal(true)}
              variant="outline"
              size="sm"
              className="w-full border-primary/50 bg-primary/30 text-card-foreground hover:bg-primary/40 hover:text-card-foreground"
            >
              <Truck className="mr-2 h-4 w-4" />
              {deliveryDetails.recipientName
                ? 'Editar Datos de Entrega'
                : 'Agregar Datos de Entrega'}
            </Button>
            {deliveryDetails.recipientName && (
              <div className="space-y-1 rounded bg-white p-2 text-xs">
                <p>
                  <strong>Destinatario:</strong> {deliveryDetails.recipientName}
                </p>
                <p>
                  <strong>Teléfono:</strong> {deliveryDetails.customerPhone}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Customer selection */}
        <div className="flex gap-2">
          <button
            onClick={() => setCustomerType('general')}
            className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${customerType === 'general'
                ? 'border-primary bg-primary/10 text-primary'
                :  'border-border text-card-foreground hover:bg-primary/5'
              }`}
          >
            <Users className="mr-1 inline h-3 w-3" />
            Público General
          </button>
          {(canAction('clients.customer.view') || canAction('pos.customer.create')) && (
          <button
            onClick={() => setCustomerType('registered')}
            className={`flex-1 rounded-lg border px-3 py-1.5 text-xs transition-all ${customerType === 'registered'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-card-foreground hover:bg-primary/5'
              }`}
          >
            <User className="mr-1 inline h-3 w-3" />
            Cliente Registrado
          </button>
          )}
        </div>

        {customerType === 'registered' && (
          <>
            <div className="flex gap-2">
              <div className="flex-1">
                <CatalogSelect
                  catalog="customers"
                  placeholder="Seleccionar cliente"
                  value={selectedCustomer || undefined}
                  onChange={(value: unknown) => setSelectedCustomer(value ? Number(value) : 0)}
                  className="h-8 text-sm"
                  itemLabelFormatter={(item: { name: string; phone?: string }) => (
                    item.phone ? `${item.name} - ${item.phone}` : item.name
                  )}
                />
              </div>
              {canAction('pos.customer.create') && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewCustomerModal(true)}
                className="h-8 border-primary/50 bg-primary/30 text-card-foreground hover:bg-primary/40 hover:text-card-foreground"
              >
                <Plus className="h-4 w-4" />
              </Button>
              )}
            </div>

            {/* Customer Points Info */}
            {!!selectedCustomer && (
              <div className="space-y-2 rounded-lg border border-primary/20 bg-card p-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 text-left"
                  aria-expanded={customerSummaryExpanded}
                  aria-controls="pos-customer-summary-details"
                  onClick={() => setCustomerSummaryExpanded((expanded) => !expanded)}
                >
                  <span className="text-sm font-medium text-card-foreground">
                    Puntos Disponibles:
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-bold text-primary">
                      {customerPoints.toLocaleString()} pts
                    </span>
                    {customerSummaryExpanded
                      ? <ChevronUp className="h-4 w-4 text-primary" />
                      : <ChevronDown className="h-4 w-4 text-primary" />}
                  </span>
                </button>
                <div
                  id="pos-customer-summary-details"
                  className={customerSummaryExpanded ? 'space-y-2' : 'hidden'}
                >
                  <div className="flex items-center justify-between text-xs text-purple-800">
                    <span>Saldo monetario:</span>
                    <strong>${loyaltyCredit.toFixed(2)}</strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-primary">Puntos a ganar:</span>
                    <span className="text-sm font-bold text-primary">+{pointsEarned} pts</span>
                  </div>

                  {canAction('pos.loyalty.approve') && (
                    <div className="border-t border-purple-200 pt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="use-points"
                          aria-label="Usar saldo/recompensa en esta compra"
                          checked={usePoints}
                          disabled={loyaltyCheckDisabled}
                          onCheckedChange={(checked) => setUsePoints(checked as boolean)}
                        />
                        <label
                          htmlFor="use-points"
                          className={loyaltyCheckDisabled
                            ? 'text-xs font-medium text-purple-900/60'
                            : 'cursor-pointer text-xs font-medium text-purple-900'}
                        >
                          {isQuotingLoyalty && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
                          Usar saldo/recompensa en esta compra
                        </label>
                      </div>
                    </div>
                  )}

                  {loyaltyMessage && (
                    <p className="text-xs leading-5 text-slate-600">{loyaltyMessage}</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cart Items */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {cart.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-card-foreground/70">El carrito está vacío</p>
          </div>
        ) : (
          <>
            {cart.some((item) => item.selected) && (
              <Button
                onClick={deleteSelectedItems}
                disabled={isUpdatingQuantities}
                variant="destructive"
                size="sm"
                className="bg-primary hover:bg-primary/80 w-full"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar Seleccionados
              </Button>
            )}

            {cart.map((item) => (
              <div key={item.id} className="relative rounded-xl border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex gap-3">
                  <div className="pt-1">
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                      className="border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                  </div>

                  {item.image ? (
                    <div className="h-14 w-14 overflow-hidden rounded-md border border-primary/20 bg-primary/10 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="h-14 w-14 flex items-center justify-center rounded-md border border-primary/20 bg-primary/10 flex-shrink-0 text-primary/50">
                      <ImageOff className="h-6 w-6" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="text-sm font-semibold text-card-foreground line-clamp-2 leading-tight">
                        {item.name}
                      </h4>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        disabled={isProductQuantityPending(item.id)}
                        className="flex-shrink-0 -mt-1 -mr-1 p-1 text-card-description transition-colors hover:text-red-500"
                        title="Eliminar del carrito"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                      <span className="font-medium text-card-foreground/70 shrink-0">
                        {item.sku || item.barcode || item.code || 'N/A'}
                      </span>
                      {item.category && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="text-card-description truncate max-w-[120px]">
                            {item.category}
                          </span>
                        </>
                      )}
                    </div>

                    {isPromotionPricing(item.pricing) && (
                      <div className="mt-2 rounded-md border border-purple-200 bg-purple-50 px-2 py-1.5 text-[11px] text-purple-800">
                        <p className="font-semibold">{pricingRuleName(item.pricing)}</p>
                        <p>{pricingRuleLabel(item.pricing)}</p>
                      </div>
                    )}

                    <div className="mt-2.5 flex items-end justify-between">
                      <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background/30 p-0.5">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md bg-card text-card-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold text-card-foreground">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="flex h-7 w-7 items-center justify-center rounded-md bg-card text-card-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      <div className="text-right">
                        <p className={`mb-0.5 text-[11px] font-medium text-card-description ${Number(item.amounts?.savings_total || 0) > 0 ? 'line-through' : ''}`}>
                          ${parseFloat(String(item.amounts?.regular_unit_total || item.price || '0').replace(/[^0-9.-]+/g, "")).toFixed(2)} c/u
                        </p>
                        {Number(item.amounts?.savings_total || 0) > 0 && (
                          <p className="mb-1 text-[11px] font-semibold text-purple-700">
                            ${Number(item.amounts?.applied_unit_total || item.price || 0).toFixed(2)} c/u aplicado
                          </p>
                        )}
                        <p className="text-[15px] font-bold text-primary leading-none">
                          ${parseFloat(String(item.total || item.amounts?.total || (item.price * item.quantity) || '0').replace(/[^0-9.-]+/g, "")).toFixed(2)}
                        </p>
                        {Number(item.amounts?.savings_total || 0) > 0 && (
                          <p className="mt-1 text-[11px] font-medium text-orange-600">
                            -${Number(item.amounts?.savings_total).toFixed(2)} ahorro
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Totals */}
      {cart.length > 0 && (
        <div className="shrink-0 space-y-3 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {savingsTotal > 0 && (
            <div className="flex justify-between text-sm text-card-description">
              <span>Precio regular (IVA incl.):</span>
              <span className="font-medium">${regularTotal.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-card-description">Subtotal:</span>
            <span className="font-medium text-card-foreground">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-card-description">IVA (16%):</span>
            <span className="font-medium text-card-foreground">
              ${tax.toFixed(2)}
            </span>
          </div>
          {saleType === 'delivery' && deliveryCost > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-card-description">Costo de Envío:</span>
              <span className="font-medium text-card-foreground">
                ${deliveryCost.toFixed(2)}
              </span>
            </div>
          )}
          {pointsDiscount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-purple-600">Saldo de lealtad aplicado:</span>
              <span className="font-medium text-purple-600">
                -${pointsDiscount.toFixed(2)}
              </span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-3">
            <span className="font-semibold text-card-foreground">Total a pagar:</span>
            <span className="text-xl font-semibold text-card-foreground">
              ${total.toFixed(2)}
            </span>
          </div>
          {savingsTotal > 0 && (
            <div className="flex justify-between rounded-md bg-purple-50 px-2 py-1.5 text-sm text-purple-700">
              <span>Ahorro promocional (IVA incl.):</span>
              <span className="font-semibold">-${savingsTotal.toFixed(2)}</span>
            </div>
          )}
          {customerType === 'registered' && selectedCustomer && (
            <div className="mt-2 rounded-lg border border-primary/20 bg-primary/10 p-2">
              <p className="text-xs text-primary">
                ✓ Cliente ganará{' '}
                <span className="font-bold">{pointsEarned} puntos</span> con
                esta compra
              </p>
            </div>
          )}

          {regulatory?.requires_prescription && (
            <div className={`mt-2 flex items-center justify-between rounded-lg border p-3 ${regulatory.is_ready_for_checkout ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
              <div className="flex items-center gap-2">
                <FileCheck2 className={`h-4 w-4 ${regulatory.is_ready_for_checkout ? 'text-green-700' : 'text-orange-700'}`} />
                <div>
                  <p className={`text-sm font-medium ${regulatory.is_ready_for_checkout ? 'text-green-400' : 'text-blue-400'}`}>
                    {regulatory.is_ready_for_checkout
                      ? `${regulatory.prescriptions_count} ${regulatory.prescriptions_count === 1 ? 'receta capturada' : 'recetas capturadas'}`
                      : 'Recetas pendientes'}
                  </p>
                  <p className="text-xs text-slate-600">{regulatory.covered_items_count} de {regulatory.required_items_count} productos cubiertos</p>
                </div>
              </div>
              {Boolean(prescriptions?.length) && onEditPrescription && (
                <Button type="button" size="sm" variant="ghost" onClick={onEditPrescription}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button>
              )}
            </div>
          )}

          <Button
            id="process-payment-button"
            onClick={onProcessPayment}
            className="w-full"
            size="lg"
            disabled={isValidatingCart || isUpdatingQuantities || !canCompleteSale || !canAction('pos.sale.create')}
          >
            {isValidatingCart || isUpdatingQuantities ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <DollarSign className="mr-2 h-5 w-5" />
            )}
            {isValidatingCart
              ? 'Validando carrito...'
              : isUpdatingQuantities
                ? 'Actualizando carrito...'
                : !canAction('pos.sale.create')
                  ? 'Sin permiso de venta'
                  : 'Procesar Pago'}
          </Button>
        </div>
      )}
    </div>
  )
}
